<script setup>
import { computed, ref, watch } from 'vue'
import {
  PID_ACCOUNT_NUMBER_MESSAGE,
  PID_SORT_CODE_MESSAGE,
  pidBankAccountNumber,
  pidSortCode,
} from '~/schemas/components/pid/potential-issues-sections/bank-validation'
import { useFormStore } from '~/stores/forms'

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
})

const store = useConveyancingCaseStore()
const formStore = useFormStore()

const clients = computed(() => {
  return store.clients
})

const clientData = ref({})
const oneOffAmounts = ref({})
const bankDetails = ref({})
const bankValidationMessages = {
  bankName: { required: 'Bank name is required.' },
  accountName: { required: 'Account name is required.' },
  sortCode: { required: 'Sort code is required.', pidSortCode: PID_SORT_CODE_MESSAGE },
  accountNumber: { required: 'Account number is required.', pidBankAccountNumber: PID_ACCOUNT_NUMBER_MESSAGE },
  bankStatement: { required: 'Please upload a bank statement dated within the last 3 months.' },
}

const getExistingValue = (path, fallback = '') => formStore.form?.data?.[path] ?? fallback
const hasPositiveShare = (client) => Number(clientData.value[parseInt(client.user_no)] || '0') > 0

watch(
  clients,
  () => {
    const initialValue = { ...clientData.value }
    const initialShare = String(100 / clients.value.length)

    // Check if there's existing form data for the shares
    const existingShares = formStore.form?.data?.['potential_issues.distribute_proceeds.shares.1'] ? formStore.form.data : null

    clients.value.forEach((client) => {
      const userNo = parseInt(client.user_no)
      const existingKey = `potential_issues.distribute_proceeds.shares.${userNo}`

      // Use existing value if present, otherwise use equal share
      if (initialValue[userNo] !== undefined) {
        initialValue[userNo] = String(initialValue[userNo])
      } else if (existingShares && existingShares[existingKey] !== undefined) {
        initialValue[userNo] = String(existingShares[existingKey])
      } else {
        initialValue[userNo] = initialShare
      }

      if (oneOffAmounts.value[userNo] === undefined) {
        oneOffAmounts.value[userNo] = getExistingValue(`potential_issues.distribute_proceeds.one_off_amounts.${userNo}`)
      }

      if (bankDetails.value[userNo] === undefined) {
        bankDetails.value[userNo] = {
          bank_name: getExistingValue(`potential_issues.distribute_proceeds.bank_details.${userNo}.bank_name`),
          account_name: getExistingValue(`potential_issues.distribute_proceeds.bank_details.${userNo}.account_name`),
          sort_code: getExistingValue(`potential_issues.distribute_proceeds.bank_details.${userNo}.sort_code`),
          account_number: getExistingValue(`potential_issues.distribute_proceeds.bank_details.${userNo}.account_number`),
        }
      }
    })
    clientData.value = initialValue
  },
  { immediate: true },
)

const totalShareAmount = computed(() => {
  return clients.value.reduce((acc, client) => {
    return acc + Number(clientData.value[parseInt(client.user_no)] || '0')
  }, 0)
})

const totalNotEqual100 = computed(() => {
  return totalShareAmount.value !== 100
})

const hiddenNotEqualValue = computed({
  get: () => (totalNotEqual100.value ? 'yes' : 'no'),
  set: () => {},
})
</script>

<template>
  <div class="mb-4 text-sm font-medium text-black">
    If any client is due a one-off payment before the split of the Net Sale Proceeds, enter that amount first. Then specify each client's share as a percentage.
  </div>
  <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
    <div v-for="(client, index) in clients" :key="index" class="border-[1px] border-solid p-4" :class="{ 'border-red-500': totalNotEqual100 }">
      <div class="mb-4 text-center font-medium text-black" :class="totalNotEqual100 ? 'text-red-500' : 'text-primary'">{{ client.name }}</div>
      <el-form-item label="One-off payment before NSP split">
        <form-kit
          v-model="oneOffAmounts[parseInt(client.user_no)]"
          type="el-input"
          input-type="number"
          money
          :name="`potential_issues.distribute_proceeds.one_off_amounts.${parseInt(client.user_no)}`"
        />
      </el-form-item>
      <div class="mb-4 text-sm font-medium text-black">Please specify the share of NSP as a percentage</div>
      <el-form-item label="Percentage">
        <form-kit
          v-model="clientData[parseInt(client.user_no)]"
          type="el-input"
          input-type="number"
          :name="`potential_issues.distribute_proceeds.shares.${parseInt(client.user_no)}`"
        >
          <template #append>%</template>
        </form-kit>
      </el-form-item>
      <div v-if="hasPositiveShare(client)">
        <div class="mb-4 mt-6 text-sm font-medium text-black">{{ client.name }}'s Bank Account Details</div>
        <el-form-item label="Bank Name" required>
          <form-kit
            v-model="bankDetails[parseInt(client.user_no)].bank_name"
            type="el-input"
            validation="required"
            validation-label="Bank name"
            validation-visibility="live"
            :validation-messages="bankValidationMessages.bankName"
            :name="`potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.bank_name`"
          />
        </el-form-item>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <el-form-item class="md:col-span-2" label="Account Name" required>
            <form-kit
              v-model="bankDetails[parseInt(client.user_no)].account_name"
              type="el-input"
              validation="required"
              validation-label="Account name"
              validation-visibility="live"
              :validation-messages="bankValidationMessages.accountName"
              :name="`potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.account_name`"
            />
          </el-form-item>
          <el-form-item label="Sort Code" required>
            <form-kit
              v-model="bankDetails[parseInt(client.user_no)].sort_code"
              type="el-input"
              validation="required|pidSortCode"
              validation-label="Sort code"
              validation-visibility="live"
              :validation-messages="bankValidationMessages.sortCode"
              :validation-rules="{ pidSortCode }"
              inputmode="numeric"
              maxlength="6"
              pattern="\d{6}"
              :name="`potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.sort_code`"
            />
          </el-form-item>
          <el-form-item label="Account Number" required>
            <form-kit
              v-model="bankDetails[parseInt(client.user_no)].account_number"
              type="el-input"
              validation="required|pidBankAccountNumber"
              validation-label="Account number"
              validation-visibility="live"
              :validation-messages="bankValidationMessages.accountNumber"
              :validation-rules="{ pidBankAccountNumber }"
              inputmode="numeric"
              maxlength="8"
              pattern="\d{8}"
              :name="`potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.account_number`"
            />
          </el-form-item>
        </div>
        <el-form-item required>
          <form-kit
            type="file-upload"
            validation="required"
            validation-label="Bank statement"
            validation-visibility="live"
            :validation-messages="bankValidationMessages.bankStatement"
            title="Please upload your Bank Statement dated in last 3 months"
            :name="`potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.bank_account.files`"
            v-bind="{ fileName: `potential_issues.distribute_proceeds.bank_details.${parseInt(client.user_no)}.bank_account.files` }"
          />
        </el-form-item>
      </div>
    </div>
    <form-kit v-model="hiddenNotEqualValue" type="hidden" name="potential_issues.distribute_proceeds.not_equal_100" />
  </div>
  <div v-if="totalNotEqual100" class="text-md mt-6 font-medium text-red-500">The total distribution of proceeds should equal 100%</div>
</template>
